import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { authAPI, affiliateAuthAPI } from '../../utils/api';
import { useTheme } from '../../contexts/ThemeContext';
import AuthFloatingControls from './AuthFloatingControls.jsx';

const ResetPassword = () => {
  const { t, i18n } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const isAffiliate = pathname.startsWith('/affiliate');
  const token = useMemo(() => new URLSearchParams(search).get('token') || '', [search]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loginPath = isAffiliate ? '/affiliate/login' : '/login';

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError(t('auth.invalidOrExpiredResetToken'));
        setValidating(false);
        return;
      }
      try {
        const api = isAffiliate ? affiliateAuthAPI : authAPI;
        await api.validateResetToken(token);
        setTokenValid(true);
      } catch (err) {
        setError(err.message || t('auth.invalidOrExpiredResetToken'));
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token, isAffiliate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      const api = isAffiliate ? affiliateAuthAPI : authAPI;
      const result = await api.resetPassword(token, newPassword);
      setSuccess(result.message || t('auth.passwordResetSuccess'));
      setTimeout(() => navigate(loginPath), 1200);
    } catch (err) {
      setError(err.message || t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      <AuthFloatingControls />
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center -mt-24">
            <img
              src={isDark ? '/images/Logo-dark.png' : '/images/Logo-light.png'}
              alt="Logo"
              className="w-48 h-48 object-contain mx-auto"
            />
          </div>
          <div className="glass-card">
            <h1 className="text-2xl font-bold mb-2 text-center">{t('auth.resetPassword')}</h1>
            <p className="text-sm text-center mb-6" style={{ color: 'var(--text-secondary)' }}>
              {t('auth.resetPasswordDescription')}
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl flex items-center space-x-2" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 rounded-xl flex items-center space-x-2" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{success}</span>
              </div>
            )}

            {validating ? (
              <p className="text-sm text-center">{t('auth.verifyingResetLink')}</p>
            ) : tokenValid ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium mb-2">{t('auth.newPassword')}</label>
                  <input
                    id="newPassword"
                    type="password"
                    className="glass-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">{t('auth.confirmNewPassword')}</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="glass-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" disabled={loading} className="glass-button w-full py-3 font-semibold disabled:opacity-50">
                  {loading ? t('auth.sending') : t('auth.resetPassword')}
                </button>
              </form>
            ) : null}

            <div className="mt-6 text-center">
              <Link to={loginPath} className="font-medium hover:underline" style={{ color: 'var(--accent-primary)' }}>
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
